import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "chart-wrapper" }
const _hoisted_2 = { class: "chart-area" }
const _hoisted_3 = ["onMouseenter"]
const _hoisted_4 = {
  key: 0,
  class: "tooltip"
}
const _hoisted_5 = { class: "chart-labels" }

import { computed, ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

type UsageTabKey = "internet" | "minutes" | "sms";


export default /*@__PURE__*/_defineComponent({
  __name: 'HistoryChart',
  props: {
    period: {},
    activeTab: {},
    operations: {}
  },
  setup(__props: any) {

const props = __props;

const store = useStore();
const { tm } = useI18n();
const colorTheme = ref<"moiOperator" | "psb" | "atom">("moiOperator");
const hoverIndex = ref<number | null>(null);

const monthNames = tm("format.monthsGenitive") as string[];

function convertToISO(dateHuman: string): string {
  // "24 октября" → "YYYY-10-24"
  const [day, monthName] = dateHuman.split(" ");
  const month = monthNames.indexOf(monthName) + 1;
  const y = new Date().getFullYear();
  return `${y}-${String(month).padStart(2, "0")}-${String(day).padStart(
    2,
    "0"
  )}`;
}

function getOperationMetricValue(op: {
  traffic: number;
  duration: number;
}): number {
  if (props.activeTab === "internet") return op.traffic || 0;
  if (props.activeTab === "minutes")
    return op.duration ? Math.ceil(op.duration / 60) : 0;
  return 1; // sms
}

function formatTooltipValue(sum: number): string {
  if (props.activeTab === "internet") {
    if (sum >= 1024 * 1024) return `${(sum / 1024 / 1024).toFixed(2)} ГБ`;
    if (sum >= 1024) return `${(sum / 1024).toFixed(1)} МБ`;
    return `${Math.round(sum)} КБ`;
  }
  if (props.activeTab === "minutes") return `${sum} мин`;
  return `${sum} SMS`;
}

// Flat list of { dateISO, metricValue }
const flat = computed(() =>
  props.operations.flatMap((day) => {
    const iso = convertToISO(day.date);
    return day.operations.map((op) => ({
      dateISO: iso,
      metricValue: getOperationMetricValue(op),
    }));
  })
);

const chartData = computed(() => {
  const result: Record<string, number> = {};
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const today = now.getDate();

  if (props.period === "week") {
    for (let i = 0; i < 7; i++) {
      const d = new Date(y, m, today - 6 + i);
      const key = `${d.getMonth() + 1}-${d.getDate()}`;
      result[key] = 0;
    }
  } else if (props.period === "month") {
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    for (let i = 1; i <= daysInMonth; i++) {
      result[i] = 0;
    }
  } else {
    for (let i = 1; i <= 12; i++) {
      result[i] = 0;
    }
  }

  // Sum metric values per slot
  flat.value.forEach((op) => {
    const d = new Date(op.dateISO);
    if (props.period === "week") {
      const key = `${d.getMonth() + 1}-${d.getDate()}`;
      if (result[key] !== undefined) result[key] += op.metricValue;
    } else if (props.period === "month") {
      const day = d.getDate();
      if (result[day] !== undefined) result[day] += op.metricValue;
    } else {
      const month = d.getMonth() + 1;
      if (result[month] !== undefined) result[month] += op.metricValue;
    }
  });

  if (props.period === "week") {
    const keys = Object.keys(result).sort((a, b) => {
      const [ma, da] = a.split("-").map(Number);
      const [mb, db] = b.split("-").map(Number);
      return (
        new Date(y, ma - 1, da).getTime() - new Date(y, mb - 1, db).getTime()
      );
    });
    return keys.map((key) => ({
      label: key.split("-")[1],
      value: result[key],
    }));
  } else {
    return Object.keys(result)
      .sort((a, b) => Number(a) - Number(b))
      .map((label) => ({ label, value: result[label] }));
  }
});

const maxValue = computed(() =>
  Math.max(...chartData.value.map((x) => x.value), 1)
);

onMounted(() => {
  colorTheme.value = store.state.brand.operatorSource;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(chartData.value, (item, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: index,
          class: _normalizeClass(["chart-bar", colorTheme.value]),
          style: _normalizeStyle({ height: `${(item.value / maxValue.value) * 100}%` }),
          onMouseenter: ($event: any) => (hoverIndex.value = index),
          onMouseleave: _cache[0] || (_cache[0] = ($event: any) => (hoverIndex.value = null))
        }, [
          (hoverIndex.value === index)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(formatTooltipValue(item.value)), 1))
            : _createCommentVNode("", true)
        ], 46, _hoisted_3))
      }), 128))
    ]),
    _createElementVNode("div", _hoisted_5, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(chartData.value, (item, index) => {
        return (_openBlock(), _createElementBlock("span", {
          key: 'lbl-' + index
        }, _toDisplayString(item.label), 1))
      }), 128))
    ])
  ]))
}
}

})